import React, { useMemo, useState } from 'react';
import type { BusinessInfo, Order } from '../../types';
import { buildSharedReceiptUrl, createSharedReceiptPayload } from '../../lib/receiptShare';

interface ReceiptQrModalProps {
  order: Order;
  businessInfo?: BusinessInfo;
  /** Optional event token/queue label to show with the receipt (e.g. from formatEventTokenBanner). */
  tokenDisplay?: string;
  /** @deprecated prefer tokenDisplay */
  tokenNumber?: number;
  /** When set, the QR encodes only this sub-bill's slice (one customer's items + share). */
  subBillId?: string;
  onClose: () => void;
}

const ReceiptQrModal: React.FC<ReceiptQrModalProps> = ({ order, businessInfo, tokenDisplay, tokenNumber, subBillId, onClose }) => {
  const [copied, setCopied] = useState(false);
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const receiptUrl = useMemo(() => {
    const payload = createSharedReceiptPayload(order, businessInfo, {
      tokenNumber,
      tokenDisplay,
      subBillId,
    });
    return buildSharedReceiptUrl(payload);
  }, [order, businessInfo, tokenNumber, tokenDisplay, subBillId]);

  const subBill = subBillId ? order.subBills?.find((s) => s.id === subBillId) : undefined;

  const qrImageUrl = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(receiptUrl)}`,
    [receiptUrl]
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 relative">
        <button type="button" onClick={onClose} className="absolute top-2 right-3 text-2xl text-gray-400 hover:text-gray-700">
          &times;
        </button>
        <h3 className="text-lg font-bold text-gray-900">
          {subBill ? `Sub-bill receipt QR — ${subBill.name || 'Guest'}` : 'Customer receipt QR'}
        </h3>
        <p className="text-sm text-gray-600 mt-1 mb-3">
          {subBill
            ? `Customer can scan this code to open the receipt for their slice (${subBill.itemAllocations.reduce((s, a) => s + a.quantity, 0)} item${subBill.itemAllocations.reduce((s, a) => s + a.quantity, 0) === 1 ? '' : 's'}, $${subBill.total.toFixed(2)}).`
            : 'Customer can scan this code to open and download the receipt (image or PDF).'}
        </p>
        <div className="flex flex-col items-center mb-3 gap-2">
          <img src={qrImageUrl} alt="Receipt QR code" className="w-64 h-64 border rounded-lg" />
          {(tokenDisplay || (typeof tokenNumber === 'number' && tokenNumber > 0)) && (
            <div className="inline-flex items-center rounded-full bg-pink-50 border border-pink-200 px-3 py-1">
              <span className="text-xs font-semibold text-gray-700 mr-1">Token</span>
              <span className="text-sm font-extrabold text-pink-700">
                {tokenDisplay ? tokenDisplay.replace(/^Token /, '') : `#${tokenNumber}`}
              </span>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 break-all mb-3">{receiptUrl}</p>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(receiptUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="w-full py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
        {isLocalhost && (
          <button
            type="button"
            onClick={() => window.open(receiptUrl, '_blank', 'noopener,noreferrer')}
            className="w-full py-2 mt-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 font-semibold"
          >
            Simulate QR scan (open receipt)
          </button>
        )}
      </div>
    </div>
  );
};

export default ReceiptQrModal;
